import { useRef, useState } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useNavigateTransition } from '../lib/navigation';
import { useFlow } from '../state/flow';
import ReportCard, { ScaledReportCard } from '../components/ReportCard';
import DownloadIcon from '../components/DownloadIcon';
import { rasterizeReportCard, quizShareFilename } from '../lib/quizShare';
import { shareOrDownload } from '../lib/shareOrDownload';
import { trackEvent } from '../lib/analytics';
import { QUIZ_COPY } from '../lib/quizCopy';
import './QuizResults.scss';

interface ResultsState {
  png?: string;
}

/** Quiz results — the on-screen report card (live, scaled) plus Download. The
 * download uses the PNG the loading screen baked (handed over via route state);
 * if absent (direct nav), an offscreen full-size card is rasterized on demand
 * (the on-screen card is transform-scaled, so it can't be the capture source). */
export default function QuizResults() {
  const { episode } = useParams();
  const ep = Number(episode);
  const navigate = useNavigateTransition();
  const location = useLocation();
  const { quizResults } = useFlow();
  const result = quizResults[ep];

  // The PNG is only downloaded, never rendered — a ref, not state.
  const handoffPng = (location.state as ResultsState | null)?.png ?? null;
  const pngRef = useRef<string | null>(handoffPng);
  const offscreenRef = useRef<HTMLDivElement | null>(null);
  const [busy, setBusy] = useState(false);

  if (!result) return <Navigate to="/class" replace />;

  async function handleDownload() {
    if (busy) return;
    setBusy(true);
    try {
      if (!pngRef.current && offscreenRef.current) {
        pngRef.current = await rasterizeReportCard(offscreenRef.current);
      }
      if (pngRef.current) {
        await shareOrDownload(pngRef.current, quizShareFilename(ep));
        trackEvent('quiz_share_downloaded', { episode: ep });
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="quiz-results">
      <div className="quiz-results__copy">
        <h1 className="quiz-results__title">{QUIZ_COPY.resultsTitle}</h1>
        <p className="quiz-results__subtitle">{QUIZ_COPY.resultsSubtitle}</p>
        <button type="button" className="quiz-results__again" onClick={() => navigate('/class')}>
          {QUIZ_COPY.takeAnother}
        </button>
      </div>

      <div className="quiz-results__card">
        <button
          type="button"
          className="quiz-results__card-btn"
          onClick={handleDownload}
          disabled={busy}
          aria-busy={busy}
          aria-label={QUIZ_COPY.share}
        >
          <ScaledReportCard result={result} variant="full" />
        </button>
        <button
          type="button"
          className="quiz-results__download"
          onClick={handleDownload}
          disabled={busy}
          aria-busy={busy}
        >
          {/* Mirrors the ID-card share tiles: "Share" on mobile (native share
              sheet), "Download" + icon on desktop. */}
          <span className="quiz-results__dl quiz-results__dl--mobile">{QUIZ_COPY.share}</span>
          <span className="quiz-results__dl quiz-results__dl--desktop">
            {QUIZ_COPY.download} <DownloadIcon size={18} />
          </span>
        </button>
      </div>

      {/* Offscreen full-size card for rasterizing the download — mounted only
          when the loading screen didn't already hand us a baked PNG. */}
      {!handoffPng && (
        <div className="quiz-results__offscreen" aria-hidden="true">
          <ReportCard ref={offscreenRef} result={result} variant="full" />
        </div>
      )}
    </div>
  );
}
